import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import api from "services/api";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";

import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import CardMedia from "@mui/material/CardMedia";

import DataTable from "examples/Tables/DataTable";
import Actionables from "components/UpStyled/Actionables";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridViewIcon from "@mui/icons-material/GridView";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import PageviewIcon from "@mui/icons-material/Pageview";
import EditIcon from "@mui/icons-material/Edit";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import NoteAddIcon from "@mui/icons-material/NoteAdd";

import SuiBox from "components/SuiBox";
import SuiAvatar from "components/SuiAvatar";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import Switch from "@mui/material/Switch";

import { capitalCase } from "capital-case";
import { AccountContext } from "context/account";
import CategoryContext from "context/category-context";
import { UserSettingContext } from "context/user-setting";

import Prompt from "components/UpStyled/Prompt";
import toast from "react-hot-toast";
import moment from "moment";
import qs from "query-string";
import { useForm, Controller } from "react-hook-form";

import { materialCategoryOptions } from "utils/data";
import { validURL } from "utils/helper";

import CreateMaterialModal from "pages/de/material-library/components/CreateMaterialModal";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import EditColumnsModal from "pages/de/material-library/components/EditColumnsModal";

const MaterialLibrary = ({ sharedView = false }) => {
  const [data, setData] = useState([]);

  const [createMaterialVisible, setCreateMaterialVisible] = useState(false);
  const [editMaterialVisible, setEditMaterialVisible] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState({});
  const [customizedColumns, setCustomizedColumns] = useState([]);

  const [deleteVisible, setDeleteVisible] = useState(false);
  const [editColumnsVisible, setEditColumnsVisible] = useState(false);

  const [loading, setLoading] = useState(false);
  const { setting, updateUserSetting } = useContext(UserSettingContext);

  useEffect(() => {
    retrieveMaterial();
  }, []);

  useEffect(() => {
    setCustomizedColumns(setting?.materialLibrary?.fields || []);
  }, [setting]);

  const retrieveMaterial = async () => {
    try {
      const { data } = await api.materialLibrary.list({
        order: "-updated_at",
      });
      setData(data?.materials || []);
    } catch (err) {
      toast.error(`Retrieve material list failed!`);
      console.log(err);
    }
  };

  const handleDeleteMaterial = async (material) => {
    try {
      await api.materialLibrary.delete(material.id);
      setDeleteVisible(false);
      retrieveMaterial();

      toast.success(`${material.name} has been removed successfully!`);
    } catch (err) {
      toast.error(`${material.name} could not be removed!`);
      console.log(err);
    }
  };
  const handleEditColumns = async (columnList) => {
    try {
      const newSetting = { ...setting };
      if (newSetting?.materialLibrary) {
        newSetting.materialLibrary.fields = columnList;
      } else {
        newSetting.materialLibrary = { fields: columnList };
      }

      await updateUserSetting({ setting: newSetting });
      setEditColumnsVisible(false);
      toast.success("Columns have been edited successfully!");
    } catch (err) {
      console.log(err);
      toast.error("Failed to edit columns!");
    }
  };

  const getMaterialTableRow = (item) => {
    const cate = materialCategoryOptions.find((x) => x.value == item?.category);
    const images = item?.metadata?.images || [];

    let rowMap = {
      key: item.id,
      name: item?.name,
      category: cate ? cate.label : item?.category,
      code: item?.code,
      images: images,
      description: item?.metadata?.description,
      created_at: item.created_at,
      updated_at: item.updated_at,
      action: item,
    };

    for (let i = 0; i < customizedColumns.length; i++) {
      let field = customizedColumns[i]?.field;
      if (field) {
        rowMap[field] = item?.metadata?.[field];
      }
    }

    return rowMap;
  };

  const columns = [
    {
      Header: "Image",
      accessor: "images",
      align: "center",
      Cell: ({ row, value }) => {
        return value && value.length > 0 ? (
          <ImageCardWrapper data={value[0]} width={50} height={50} />
        ) : null;
      },
    },
    {
      Header: "Name",
      accessor: "name",
      align: "center",
    },
    {
      Header: "Category",
      accessor: "category",
    },
    {
      Header: "Code",
      accessor: "code",
    },
    {
      Header: "Description",
      accessor: "description",
      Cell: ({ row, value }) => {
        return (
          <Tooltip title={value}>
            <div
              style={{
                wordBreak: "break-word",
                maxWidth: 200,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "1",
                WebkitBoxOrient: "vertical",
              }}
            >
              {value}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const lastColumns = [
    {
      Header: "Last Edit",
      accessor: "updated_at",
      Cell: ({ row, value }) => {
        return value ? moment(value).format("MM/DD/YY") : "";
      },
    },
    {
      Header: "More",
      accessor: "action",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => (
        <SuiBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          <Tooltip title="Edit">
            <SuiBox>
              <EditIcon
                fontSize={"small"}
                color="dark"
                sx={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedMaterial(value);
                  setEditMaterialVisible(true);
                }}
              />{" "}
            </SuiBox>
          </Tooltip>
          |{" "}
          <Tooltip title="Delete">
            <SuiBox>
              <DeleteForeverRoundedIcon
                fontSize={"small"}
                color="error"
                sx={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedMaterial(value);
                  setDeleteVisible(true);
                }}
              />{" "}
            </SuiBox>
          </Tooltip>
        </SuiBox>
      ),
    },
  ];

  const addedColumns = customizedColumns.map((x) => ({
    Header: x.headerName,
    accessor: x.field,
    Cell: ({ row, value }) => (
      <div style={{ maxWidth: 140 }}>
        {validURL(value) ? (
          <a target="_blank" href={value} onClick={(e) => e.stopPropagation()}>
            {value}
          </a>
        ) : (
          value
        )}
      </div>
    ),
  }));

  const allColumns = [...columns, ...addedColumns, ...lastColumns];

  const refresh = () => {
    retrieveMaterial();
  };

  const rows = data.map((item) => getMaterialTableRow(item));

  const handleRowClick = (row) => {
    setEditMaterialVisible(true);
    setSelectedMaterial(row?.original.action);
  };

  const HeaderTool = (
    <SuiBox display="flex" gap={1}>
      <SuiButton color="dark" variant="text" onClick={() => setEditColumnsVisible(true)}>
        <EditIcon />
        Edit columns
      </SuiButton>
    </SuiBox>
  );

  return (
    <SuiBox my={1}>
      <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={loading}>
        <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
      </Backdrop>
      <SuiBox display="flex" justifyContent="space-between" pb={1}>
        <SuiButton
          variant="gradient"
          color={"dark"}
          onClick={() => {
            setCreateMaterialVisible(true);
          }}
        >
          New Material
        </SuiButton>
      </SuiBox>

      <Card sx={{ p: 3 }}>
        <DataTable
          table={{ columns: allColumns, rows: rows }}
          entriesPerPage={{ defaultValue: 20 }}
          noEndBorder
          handleRowClick={handleRowClick}
          canSearch
          HeaderTool={HeaderTool}
        />
      </Card>
      {createMaterialVisible ? (
        <CreateMaterialModal
          handleClose={() => setCreateMaterialVisible(false)}
          visible={createMaterialVisible}
          isEdit={false}
          handleRefresh={refresh}
          customizedColumns={customizedColumns}
        />
      ) : null}
      {editMaterialVisible && selectedMaterial ? (
        <CreateMaterialModal
          handleClose={() => setEditMaterialVisible(false)}
          visible={editMaterialVisible}
          isEdit={true}
          handleRefresh={refresh}
          material={selectedMaterial}
          customizedColumns={customizedColumns}
        />
      ) : null}

      {deleteVisible && selectedMaterial ? (
        <Prompt
          heading="Delete Material"
          text={`Are you sure you want to delete: ${selectedMaterial.name}`}
          visible={deleteVisible}
          okText="Delete"
          okColor="error"
          handleOk={() => handleDeleteMaterial(selectedMaterial)}
          handleClose={() => setDeleteVisible(false)}
        />
      ) : null}
      {editColumnsVisible ? (
        <EditColumnsModal
          handleClose={() => setEditColumnsVisible(false)}
          visible={editColumnsVisible}
          handleOk={handleEditColumns}
          columns={customizedColumns}
        />
      ) : null}
    </SuiBox>
  );
};

export default MaterialLibrary;
